import React from 'react';
import { useIntl } from 'react-intl';
import { TRPCClientErrorLike } from '@trpc/client';
import { DefaultErrorShape } from '@trpc/server/unstable-core-do-not-import';
import { faUsers } from '@fortawesome/pro-light-svg-icons/faUsers';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLEmptyState } from '../SMLEmptyState/SMLEmptyState';
import { ReachStat } from './ReachStat';
import { ReachTrendChart } from './ReachTrendChart';
import { ReachCardSeriesPoint, ReachCardSummary } from './types';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';

interface ReachCardProps {
  entityType: 'track' | 'artist';
  summary?: ReachCardSummary;
  series: ReachCardSeriesPoint[];
  isLoading: boolean;
  error: TRPCClientErrorLike<{ transformer: true; errorShape: DefaultErrorShape }> | null;
  retry: () => void;
}

// A brand-new entity must never render as "reaches nobody" — an empty
// series means no data yet, so it always renders the empty state below,
// never a stat that reads "0 people".
const ReachCardComponent: React.FC<ReachCardProps> = ({ entityType, summary, series, isLoading, error, retry }) => {
  const intl = useIntl();
  const isTrack = entityType === 'track';

  const title = isTrack
    ? intl.formatMessage({ id: 'app.sml.track.details.reachCard.title', defaultMessage: 'Audience reach' })
    : intl.formatMessage({ id: 'app.sml.artist.details.reachCard.title', defaultMessage: 'Audience reach' });

  const noDataTitle = isTrack
    ? intl.formatMessage({
        id: 'app.sml.track.details.reachCard.noData',
        defaultMessage: 'Not enough data to show reach yet',
      })
    : intl.formatMessage({
        id: 'app.sml.artist.details.reachCard.noData',
        defaultMessage: 'Not enough data to show reach yet',
      });

  const hasData = series.length > 0 && summary !== undefined;

  return (
    <Panel className="h-100">
      <PanelHead title={title} />
      <PanelBody>
        {isLoading ? (
          <div style={{ minHeight: '260px' }}>
            <LoadingView small />
          </div>
        ) : error !== null ? (
          <ErrorView error={error} retry={retry} />
        ) : !hasData ? (
          <SMLEmptyState icon={faUsers} title={noDataTitle} />
        ) : (
          <>
            <ReachStat entityType={entityType} summary={summary} />
            <ReachTrendChart series={series} />
          </>
        )}
      </PanelBody>
    </Panel>
  );
};

export const ReachCard = TypedMemo(ReachCardComponent);
